import { isPast } from 'date-fns';
import {
  type CommunitySlim,
  type ContentObject,
  type ContentObjectList,
  type PollContentObjectList,
  type Profile,
  type ProgramContentObject,
  isCommunityContentObject,
  isGroupContentObject,
  isJournalNote,
  isNodeEventContentObject,
  isProgramEventInProgress,
} from 'models';
import type { User } from 'features/auth';
import { canRepostContentObject } from '../can-repost-content-object';
import { isGroupAdmin, isProgramAdminProfile } from '../user-roles';
import {
  isCommunityAdminProfile,
  isCommunityOrOrganizationAdmin,
  isOrganizationAdminProfile,
  isSuperAdmin,
} from '../utils';
import { isContentObjectAuthorProfile } from './is-content-object-author';

const isContentManager = ({ contentObject, viewer }: { contentObject: ContentObject; viewer: User }) =>
  isSuperAdmin({ viewer: viewer.railsUserData }) ||
  isContentObjectAuthor({ contentObject, viewer }) ||
  (!isGroupContentObject(contentObject) &&
    !isJournalNote(contentObject) &&
    !isNodeEventContentObject(contentObject) &&
    isOrganizationAdminProfile({ organizationId: contentObject.organization.id, viewer: viewer.railsUserData }));

export const canManageContentObject = ({ contentObject, viewer }: { contentObject: ContentObject; viewer: User }) => {
  if (contentObject.contentableType === 'Program') {
    return (
      isContentManager({ contentObject, viewer }) ||
      isProgramAdminProfile({ programId: contentObject.program.id, viewer: viewer.railsUserData })
    );
  }

  if (contentObject.contentableType === 'Organization') {
    return isContentManager({ contentObject, viewer });
  }

  if (contentObject.contentableType === 'Group') {
    return (
      isContentManager({ contentObject, viewer }) ||
      isGroupAdmin({ groupId: contentObject.group.id, viewer: viewer.railsUserData })
    );
  }

  const [community] = contentObject.postInCommunities ?? [];

  return (
    isContentManager({ contentObject, viewer }) ||
    Boolean(community && isCommunityAdminProfile({ communityId: community.id, viewer: viewer.railsUserData }))
  );
};

export const canReportContentObject = ({
  contentObject,
  viewer,
}: {
  contentObject: { author: { id: string } };
  viewer: User;
}) => !isContentObjectAuthor({ contentObject, viewer });

export const isContentObjectAuthor = ({
  contentObject,
  viewer: { railsUserData },
}: {
  contentObject: { author: { id: string } };
  viewer: User;
}) => railsUserData.id === contentObject.author.id;

export const canShareContentObject = ({ contentObject, viewer }: { contentObject: ContentObject; viewer: User }) =>
  canRepostContentObject({ contentObject, viewer: viewer.railsUserData });

export const canCreateContentObject = ({ viewer }: { viewer: User }): boolean =>
  Boolean(viewer.railsUserData.permissions.createContentObject);

export const canCommentContentObjectList = ({
  contentObject,
  viewer,
}: {
  contentObject: ContentObjectList | ProgramContentObject;
  viewer: User;
}) => {
  const { railsUserData: profile } = viewer;

  if ('type' in contentObject && contentObject.type === 'giving') {
    return false;
  }

  if (isSuperAdmin({ viewer: profile })) {
    return true;
  }

  if (contentObject.contentableType === 'Program') {
    return (
      Boolean(contentObject.program.settings?.allowComments) ||
      isProgramAdminProfile({ programId: contentObject.program.id, viewer: profile }) ||
      isOrganizationAdminProfile({ organizationId: contentObject.organization.id, viewer: profile })
    );
  }

  if (isCommunityContentObject({ contentObject })) {
    const community = contentObject.postInCommunities?.[0];

    return Boolean(
      community &&
      (community.settings.allowComments ||
        isCommunityAdminProfile({ communityId: community.id, viewer: profile }) ||
        isOrganizationAdminProfile({ organizationId: community.organizationId, viewer: profile })),
    );
  }

  return false;
};

export const canRepostContentObjectList = ({
  contentObject,
  viewer,
}: {
  contentObject: ContentObjectList;
  viewer: User;
}) => {
  if (contentObject.contentableType !== 'Community') {
    return false;
  }

  const [community] = contentObject.postInCommunities;

  if (community.privacy === 'secret') {
    return false;
  }

  return (
    community.privacy === 'public' ||
    isSuperAdmin({ viewer: viewer.railsUserData }) ||
    isCommunityOrOrganizationAdmin({ community, viewer: viewer.railsUserData })
  );
};

export const canShareContentObjectList = ({
  contentObject,
  viewer,
}: {
  contentObject: ContentObjectList;
  viewer: User;
}) => canRepostContentObjectList({ contentObject, viewer });

export const canViewPollChoicePercentage = ({
  contentObject,
  viewer,
}: {
  contentObject: PollContentObjectList;
  viewer: Profile;
}) =>
  contentObject.author.id === viewer.id ||
  (contentObject.contentableType === 'Community' &&
    (isSuperAdmin({ viewer }) ||
      isCommunityOrOrganizationAdmin({ community: contentObject.postInCommunities[0], viewer })));

export const canEditContentObjectProfile = ({
  contentObject,
  viewer,
}: {
  contentObject: ContentObject;
  viewer: Profile;
}): boolean => {
  if (contentObject.contentableType === 'Program') {
    return false;
  }

  switch (contentObject.type) {
    case 'comment':
    case 'giving':
    case 'video':
      return false;
    case 'event': {
      const isEventStartInPast = isPast(contentObject.event.startTime);
      const isEventWithinTime = isProgramEventInProgress({
        endDifferenceInMinutes: 0,
        endTime: contentObject.event.endTime,
        startTime: contentObject.event.startTime,
      });

      if (isEventStartInPast && !isEventWithinTime) {
        return false;
      }

      return canManageContentObjectOrCommunity({ contentObject, viewer });
    }
    case 'kudo':
    case 'poll':
    case 'post':
      return canManageContentObjectOrCommunity({ contentObject, viewer });
  }
};

export const canDeleteContentObjectProfile = ({
  contentObject,
  viewer,
}: {
  contentObject: ContentObject;
  viewer: Profile;
}): boolean => {
  if (contentObject.contentableType === 'Program') {
    return false;
  }

  switch (contentObject.type) {
    case 'comment':
    case 'giving':
    case 'video':
      return false;
    case 'event': {
      const isProgramEventLive = isProgramEventInProgress({
        endTime: contentObject.event.endTime,
        startTime: contentObject.event.startTime,
      });

      if (isProgramEventLive) {
        return false;
      }

      return canManageContentObjectOrCommunity({ contentObject, viewer });
    }
    case 'kudo':
    case 'poll':
    case 'post':
      return canManageContentObjectOrCommunity({ contentObject, viewer });
  }
};

const canManageContentObjectOrCommunity = ({
  contentObject,
  viewer,
}: {
  contentObject: ContentObject;
  viewer: Profile;
}): boolean => {
  const community: CommunitySlim | undefined =
    contentObject.contentableType === 'Community' ? contentObject.postInCommunities[0] : undefined;

  return (
    isSuperAdmin({ viewer }) ||
    isContentObjectAuthorProfile({ contentObject, viewer }) ||
    Boolean(community && canManageContentObjectCommunity({ community, viewer }))
  );
};

const canManageContentObjectCommunity = ({
  community,
  viewer,
}: {
  community: CommunitySlim;
  viewer: Profile;
}): boolean =>
  isCommunityAdminProfile({ communityId: community.id, viewer }) ||
  isOrganizationAdminProfile({ organizationId: community.organizationId, viewer });

type LegacyContentObjectForComments = {
  contentable_type?: string;
  organization: { id: string };
  post_in_communities?: Array<{ id: string; organization_id: string; settings: { allow_comments: boolean } }>;
  program?: { id: string; settings?: { allow_comments: boolean } };
  type?: string;
};

/**
 * Serves the two remaining snake_case call sites in old/atoms/courses-timeline:
 * shared/Footer.jsx and ActionsRenderer.jsx. Delete together with courses-timeline.
 */
export const canCommentLegacyContentObject = ({
  contentObject,
  viewer,
}: {
  contentObject: LegacyContentObjectForComments;
  viewer: Profile;
}) => {
  if (contentObject.type === 'giving') {
    return false;
  }

  if (isSuperAdmin({ viewer })) {
    return true;
  }

  if (contentObject.contentable_type === 'Program' && contentObject.program) {
    return (
      Boolean(contentObject.program.settings?.allow_comments) ||
      isProgramAdminProfile({ programId: contentObject.program.id, viewer }) ||
      isOrganizationAdminProfile({ organizationId: contentObject.organization.id, viewer })
    );
  }

  if (contentObject.contentable_type === 'Community' && contentObject.post_in_communities) {
    return contentObject.post_in_communities.some(
      (community) =>
        community.settings.allow_comments ||
        isCommunityAdminProfile({ communityId: community.id, viewer }) ||
        isOrganizationAdminProfile({ organizationId: community.organization_id, viewer }),
    );
  }

  return false;
};

/**
 * Serves the two remaining snake_case call sites in old/atoms/courses-timeline:
 * shared/Footer.jsx and ActionsRenderer.jsx. Delete together with courses-timeline.
 */
export const canReportLegacyContentObject = ({
  contentObject,
  viewer,
}: {
  contentObject: { author: { id: string } };
  viewer: User;
}) => !isContentObjectAuthor({ contentObject, viewer });
